import React, {
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

import type { Property } from 'csstype';
import PropTypes from 'prop-types';

import { BasicButton } from '@adsk/alloy-react-button';
import {
  AlertInformationIcon,
  AlertWarningIcon,
  CheckmarkCircleIcon,
  XCircleIcon,
  XIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import BannerDivider from './BannerDivider';
import { BannerVariant, BANNER_VARIANTS } from './consts';

type BannerProps = StylableComponent<HTMLDivElement> & {
  renderIcon?: RenderIconType;
  variant?: BannerVariant;
  onClose?: () => void;
  message?: ReactNode;
  open?: boolean;
  inline?: boolean;
};

export const mapVariantToIconRenderer = {
  [BANNER_VARIANTS.PRIMARY]: AlertInformationIcon.renderIcon,
  [BANNER_VARIANTS.SUCCESS]: CheckmarkCircleIcon.renderIcon,
  [BANNER_VARIANTS.WARNING]: AlertWarningIcon.renderIcon,
  [BANNER_VARIANTS.ERROR]: XCircleIcon.renderIcon,
};

const VISIBLE_STATE = 'visible';
const HIDDEN_STATE = 'hidden';

/**
 * Banners are notifications alerting an event that requires a user action.
 * They are similar toasts except banners are not sparked by the user's action,
 * therefore they require a different design.
 * Banners are primarily used for global messages about the system that can be simply informational (blue)
 *  or color coded to signal success (green), warning (yellow) and error (red).
 */
const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      style,
      className,
      renderIcon,
      variant = BANNER_VARIANTS.PRIMARY,
      onClose,
      message,
      open = true,
      inline,
      children,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const {
      bannerTokens,
      variantBg,
      variantBorderColor,
      variantIconBg,
      containerStyles,
      inlineStyles,
      openStyles,
      onCloseStyles,
      actionsContainerStyles,
    } = useMemo(() => {
      const bannerTokens = theme.tokens?.component?.banner;
      const variantTokens = bannerTokens?.variant?.[variant];

      const variantBg = variantTokens?._?.backgroundColor;
      const variantBorderColor = variantTokens?._?.borderColor;
      const variantIconBg = variantTokens?.icon?.backgroundColor;

      const containerStyles = bannerTokens?.container?._;
      const inlineStyles =
        bannerTokens?.container?.inline?.[inline ? 'true' : 'false'];
      const openStyles =
        bannerTokens?.container?.open?.[open ? 'true' : 'false'];
      const onCloseStyles =
        bannerTokens?.container?.onClose?.[onClose ? 'true' : 'false'];
      const messageStyles =
        bannerTokens?.actionsContainer?.message?.[message ? 'true' : 'false'];

      return {
        bannerTokens,
        variantBg,
        variantBorderColor,
        variantIconBg,
        containerStyles,
        inlineStyles,
        openStyles,
        onCloseStyles,
        actionsContainerStyles: [
          messageStyles,
          bannerTokens?.actionsContainer?._,
        ],
      };
    }, [theme.tokens, variant, inline, open, onClose, message]);

    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const [visibleState, setVisibleState] = useState<
      Property.Visibility | undefined
    >(VISIBLE_STATE);

    const transitionStart = useCallback(() => {
      if (open) {
        setVisibleState(VISIBLE_STATE);
      }
    }, [open]);

    useEffect(() => {
      document.addEventListener('transitionstart', transitionStart);

      if (open) {
        setVisibleState(VISIBLE_STATE);
      } else {
        setVisibleState(HIDDEN_STATE);
      }

      return () => {
        document.removeEventListener('transitionstart', transitionStart);
      };

      // we only want to react to changes in the open prop on component mounted
      // because subsequent changes of open need to be handled by the transition
      // start and complete handlers to maintain the animation feature of the banner
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (open) {
        setVisibleState(VISIBLE_STATE);
      }
    }, [open]);

    const transitionComplete = useCallback(() => {
      if (!open) {
        setVisibleState(HIDDEN_STATE);
      }
    }, [open]);

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          containerStyles,
          inlineStyles,
          openStyles,
          onCloseStyles,
          {
            backgroundColor: variantBg,
            borderColor: variantBorderColor,
            visibility: visibleState,
          },
          style,
        ]}
        onTransitionEnd={transitionComplete}
      >
        <div
          css={[
            bannerTokens?.iconContainer,
            { backgroundColor: variantIconBg },
          ]}
        >
          {renderIcon?.({}) || mapVariantToIconRenderer[variant]?.({})}
        </div>
        {message && (
          <div dir="auto" css={bannerTokens?.messageContainer}>
            {message}
          </div>
        )}
        <div css={actionsContainerStyles}>{children}</div>
        {children && onClose && <BannerDivider variant={variant} />}
        {onClose && (
          <BasicButton
            onClick={onClose}
            style={bannerTokens?.closeButton}
            aria-label={T.translate('CLOSE_BANNER')?.toString()}
          >
            <XIcon />
          </BasicButton>
        )}
      </div>
    );
  },
);

Banner.displayName = 'Banner';

Banner.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** render prop to display an icon on the left */
  renderIcon: PropTypes.func,
  /** Banner's variant, defaults to 'primary' */
  variant: PropTypes.oneOf(Object.values(BANNER_VARIANTS)),
  /** Banner's message to display */
  message: PropTypes.any,
  /** Toggles the Banner's visibility */
  open: PropTypes.bool,
  /** A Trigger for the close button in the Banner */
  onClose: PropTypes.func,
  /** Whether or not to use inline Banner (rounded corners, etc.)  */
  inline: PropTypes.bool,
  /** The component's children, here you should put the actions */
  children: PropTypes.any,
};

export default Object.assign(Banner, { VARIANTS: BANNER_VARIANTS });
